import { useVolunteersImportMutation } from 'common-front/src/generated/graphqlHooks';
import { useHeavent } from 'common-front/src/hooks/useHeavent';
import { MappableImportWizard } from 'common-front/src/import/mappableImportWizard';
import { ImportFieldCategory } from 'common-front/src/import/types';
import {
    AccreditationImportFragment,
    CustomFieldImportFragment,
    CustomFieldVariety
} from 'common/src/generated/types';
import { VolunteersImportInputService } from 'common/src/input/volunteersImportInput';
import { DateTimeService } from 'common/src/services/dateTimeService';
import { useService, useTranslate } from 'common/src/util/dependencies/dependencies';
import { CommunityPaths } from 'common/src/util/paths/communityPaths';
import { HeaventPaths } from 'common/src/util/paths/heaventPaths';
import { fullName } from 'common/src/vo/accreditationSlot';
import * as React from 'react';
import {
    useEventVolunteersImportInfosQuery,
    useOrganizationImportInfosQuery
} from '../../generated/graphqlHooks';
import { useNotificationContext } from '../../notifications/notificationContext';

interface IVolunteersImportProps {
    accreditations: AccreditationImportFragment[];
    closePath: string;
    customFields: CustomFieldImportFragment[];
}
const VolunteersImport = ({ accreditations, closePath, customFields }: IVolunteersImportProps) => {
    const translate = useTranslate();
    const dateTimeService = useService(DateTimeService);
    const { mutate } = useVolunteersImportMutation();
    const { checkVolunteersImport } = useNotificationContext();

    const accreditationsCategories: ImportFieldCategory[] = React.useMemo(
        () =>
            accreditations.map((accreditation) => ({
                ...accreditation,
                fields: accreditation.slots.map((slot) => ({
                    id: slot.id,
                    slug: `accreditation_slot_${slot.id}`,
                    name: fullName(dateTimeService, slot, accreditation.name, {
                        accreditation: { includeNameAtEnd: true }
                    })
                }))
            })),
        [accreditations]
    );

    return (
        <MappableImportWizard
            ImportInputService={VolunteersImportInputService}
            additionalCategories={accreditationsCategories}
            checkImport={checkVolunteersImport}
            closePath={closePath}
            createUpdateKey={{
                slug: 'email',
                name: translate('email_address_53451')
            }}
            customFields={customFields}
            importTagPrefix="volunteers"
            itemName={{ singular: translate('membre_53723'), plural: translate('membres_67337') }}
            localStorageKeyPrefix="VOLUNTEERS_IMPORT"
            mutate={mutate}
            title={translate('import_de_membr_48618')}
        />
    );
};

export const OrganizationVolunteersImport = () => {
    const {
        params: { organizationId }
    } = useHeavent();
    const { data, loader } = useOrganizationImportInfosQuery({
        organizationId,
        variety: CustomFieldVariety.UserInfo
    });

    return (
        loader || (
            <VolunteersImport
                accreditations={[]}
                closePath={CommunityPaths.COMMUNITY_USERS({ organizationId })}
                customFields={data.organization.customFields.nodes}
            />
        )
    );
};

export const EventVolunteersImport = () => {
    const {
        params: { organizationId, eventId }
    } = useHeavent();
    const { data, loader } = useEventVolunteersImportInfosQuery({
        organizationId,
        variety: CustomFieldVariety.UserInfo,
        eventId
    });

    return (
        loader || (
            <VolunteersImport
                accreditations={data.event.accreditations.nodes}
                closePath={HeaventPaths.VOLUNTEERS(organizationId, eventId)}
                customFields={data.organization.customFields.nodes}
            />
        )
    );
};
